import { parseISO } from "date-fns";
import { autoFormatCamelCase } from "@entrilia/shared/utilities/stringHelper";
import { nameof } from "@entrilia/shared/utilities/typeHelper";
import type {
  SearchWorkbookRunsRequest,
  WorkbookRun,
  WorkbookRunStatus,
} from "../../../../api/types/workbookRuns.types";
import { allWorkbookRunStatuses } from "../../../../api/types/workbookRuns.types";
import type { FilterState } from "../../../common/filters/filterState";
import type {
  DateFilter,
  GridSortItem,
  MultiSelectFilter,
  TableFilter,
  TableFilterDefinition,
  TextFilter,
} from "../../../common/filters/filterTypes";

const displayableStatuses = allWorkbookRunStatuses.filter((status) =>
  (["InProgress", "Completed", "Error", "Canceled", "Queued"] as WorkbookRunStatus[]).includes(status)
);

export const filterDefinitions: TableFilterDefinition<WorkbookRun>[] = [
  {
    id: nameof<WorkbookRun>("status"),
    name: "Status",
    type: "multi_select",
    operatorOptions: ["one_of"],
    predefinedOptions: displayableStatuses.map((status) => ({
      value: status,
      label: autoFormatCamelCase(status),
    })),
    getFieldValue: (row) => row.status,
  },
  {
    id: nameof<WorkbookRun>("createdBy"),
    name: "Created by",
    type: "text",
    operatorOptions: ["contains"],
  },
  {
    id: nameof<WorkbookRun>("createdAt"),
    name: "Created At",
    type: "date",
    operatorOptions: ["range"],
    getFieldValue: (row) => parseISO(row.createdAt),
  },
  {
    id: nameof<WorkbookRun>("startedAt"),
    name: "Started At",
    type: "date",
    operatorOptions: ["range"],
    getFieldValue: (row) => (row.startedAt ? parseISO(row.startedAt) : undefined),
  },
  {
    id: nameof<WorkbookRun>("templateId"),
    name: "Template",
    type: "multi_select",
    operatorOptions: ["one_of"],
    asyncOptions: true,
    getFieldValue: (row) => row.templateId,
  },
];

export const getWorkbookRunsFilterPayload = (
  filterState: FilterState<WorkbookRun>
): Omit<SearchWorkbookRunsRequest, "range"> => {
  const getMultiSelectFilterValues = (filterId: keyof WorkbookRun, visibleFilters: TableFilter<WorkbookRun>[]) => {
    const filter = visibleFilters.find((f) => f.id === filterId) as MultiSelectFilter<WorkbookRun> | undefined;
    const selectedValues = filter?.value?.operator === "one_of" ? filter.value.selectedValues : undefined;
    return selectedValues?.length ? selectedValues : undefined;
  };
  const getDateRangeFilterValues = (filterId: keyof WorkbookRun, visibleFilters: TableFilter<WorkbookRun>[]) => {
    const filter = visibleFilters.find((f) => f.id === filterId) as DateFilter<WorkbookRun> | undefined;
    if (filter?.value?.operator !== "range") {
      return [undefined, undefined];
    }

    const { range } = filter.value;
    const from = range?.[0];
    const to = range?.[1];
    return [from || undefined, to || undefined];
  };

  const getCreatedByFilterValue = (visibleFilters: TableFilter<WorkbookRun>[]) => {
    const filter = visibleFilters.find((f) => f.id === "createdBy") as TextFilter<WorkbookRun> | undefined;
    const value = filter?.value?.operator === "contains" ? filter.value.text : undefined;
    return value || undefined;
  };

  const getSorting = (gridSortItem: GridSortItem | undefined) => {
    if (gridSortItem === undefined) {
      return undefined;
    }

    return {
      fieldId: gridSortItem.field,
      ascending: gridSortItem.sort === "asc",
    };
  };
  const templateIds = getMultiSelectFilterValues("templateId", filterState.visibleFilters);
  const [startedAtFrom, startedAtTo] = getDateRangeFilterValues("startedAt", filterState.visibleFilters);
  const [createdAtFrom, createdAtTo] = getDateRangeFilterValues("createdAt", filterState.visibleFilters);
  const statuses = getMultiSelectFilterValues("status", filterState.visibleFilters) as WorkbookRunStatus[] | undefined;
  const createdBy = getCreatedByFilterValue(filterState.visibleFilters);
  const sort = getSorting(filterState.sorting);

  return {
    searchTerm: filterState.search.value || undefined,
    templateIds,
    startedAtFrom,
    startedAtTo,
    createdAtFrom,
    createdAtTo,
    statuses,
    createdBy,
    sort,
  };
};
